import { ChevronLeftIcon } from "@heroicons/react/outline";
import React, { useEffect, useRef } from "react";
import { cn } from "ui/lib/utils";
import { IconButton } from "./icon-button";
import { captureOpener, registerFocusHost, returnFocus } from "./return-focus";
import { useHideAppHeader, useHideTabBar } from "./tab-bar-context";

/** Inside a split view's pane, screens fill the pane instead of the screen. */
export const PushedScreenContained = React.createContext(false);

type HeaderProps = {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Without it there is no back button (a tablet's room beside its inbox). */
  onBack?: () => void;
  /** The back button's accessible name, e.g. "Back to Blocks". */
  backLabel?: string;
  leading?: React.ReactNode;
  trailing?: React.ReactNode;
  /** The title is the screen's h1, for screens that have no other. */
  heading?: boolean;
  /** Kept out of sight while the screen's own large title is on screen. */
  titleHidden?: boolean;
  className?: string;
};

/** A pushed screen's 44pt bar: Back, the title and subtitle, and actions. */
export const PushedHeader = ({
  title,
  subtitle,
  onBack,
  backLabel = "Back",
  leading,
  trailing,
  heading,
  titleHidden,
  className,
}: HeaderProps) => {
  const Title = heading ? "h1" : "h2";
  return (
    <header
      className={cn(
        "flex h-11 shrink-0 items-center gap-1 border-b border-white/[0.06] pr-1",
        className
      )}
    >
      {onBack ? (
        <IconButton label={backLabel} onClick={onBack} className="h-11 w-11">
          <ChevronLeftIcon className="size-6 text-white/90" />
        </IconButton>
      ) : (
        <span className="w-3" />
      )}
      {leading ? <span className="shrink-0">{leading}</span> : null}
      <div
        aria-hidden={titleHidden || undefined}
        className={cn(
          "min-w-0 flex-1 pl-1 transition-opacity duration-150 motion-reduce:transition-none",
          titleHidden && "opacity-0"
        )}
      >
        <Title className="truncate text-[1rem] font-semibold leading-tight text-white/90">
          {title}
        </Title>
        {subtitle ? (
          <p className="truncate text-[0.75rem] leading-tight text-faint">
            {subtitle}
          </p>
        ) : null}
      </div>
      {trailing ? (
        <div className="flex shrink-0 items-center">{trailing}</div>
      ) : null}
    </header>
  );
};

// Screen readers start on the new screen, the way a pushed page reads, and
// land back on what opened it when it pops.
const useFocusOnPush = (ref: React.RefObject<HTMLElement>) => {
  useEffect(() => {
    const opener = captureOpener();
    registerFocusHost(ref.current, opener);
    ref.current?.focus({ preventScroll: true });
    return () => {
      // Deferred: the screen under this one is still inert as it unmounts.
      setTimeout(() => returnFocus(opener, false));
    };
  }, []);
};

type PushedScreenProps = HeaderProps & {
  /** Read as the screen's name when the title isn't plain text. */
  label: string;
  children: React.ReactNode;
  /** Covered by a screen pushed over it, so out of reach until that pops. */
  inert?: boolean;
  /** Fill the nearest positioned box (a split view's pane), not the screen. */
  contained?: boolean;
  className?: string;
};

/**
 * A full-screen drill-in over the phone shell: its own bar with back replaces
 * the app header, and the body fills the rest of the screen. It sits at the
 * shell header's z-index and later in the DOM, so it covers the header while
 * sheets, which portal to the end of body, still open over it.
 */
export const PushedScreen = ({
  label,
  children,
  inert,
  contained,
  className,
  ...header
}: PushedScreenProps) => {
  const inPane = React.useContext(PushedScreenContained);
  const fill = contained ?? inPane;
  const ref = useRef<HTMLElement>(null);
  useFocusOnPush(ref);
  return (
    <section
      ref={ref}
      tabIndex={-1}
      aria-label={label}
      inert={inert}
      className={cn(
        "inset-0 z-50 flex flex-col bg-background outline-none animate-in fade-in-0 slide-in-from-right-8 duration-200 motion-reduce:animate-none",
        fill ? "absolute" : "fixed pt-[env(safe-area-inset-top)]",
        className
      )}
    >
      <PushedHeader {...header} />
      <div className="relative flex min-h-0 flex-1 flex-col">{children}</div>
    </section>
  );
};

/**
 * The same bar for a pushed page that scrolls with the document: it sticks
 * to the top and stands in for the app header while mounted. The tabs stay,
 * unless the screen owns the bottom edge (an editor, a composer, a mode bar).
 * Below lg only.
 */
export const PushedBar = ({
  className,
  ownsBottomEdge,
  ...header
}: HeaderProps & { className?: string; ownsBottomEdge?: boolean }) => {
  useHideAppHeader();
  useHideTabBar(!!ownsBottomEdge);
  const ref = useRef<HTMLDivElement>(null);
  useFocusOnPush(ref);
  return (
    <div
      ref={ref}
      tabIndex={-1}
      className={cn(
        "sticky top-0 z-30 bg-background pt-[env(safe-area-inset-top)] pl-[max(0px,env(safe-area-inset-left))] pr-[max(0px,env(safe-area-inset-right))] outline-none lg:hidden",
        className
      )}
    >
      <PushedHeader {...header} />
    </div>
  );
};
